import { Dec } from '../../shared/decimal'
import type { TickCtx, GameState } from '../tickTypes'

// updateSmall section: cosmic plague -> oganesson -> light/dark essence ->
// death essence -> finality essence -> finality cubes.
// Oracle: script.js:1985-2010 (plague), :2011-2026 (oganesson), :2027-2071
// (light/dark essence), :2072-2114 (death essence), :2115-2142 (nuclear
// pasta — ENTIRELY DOM-only, nothing to transcribe), :2143-2161 (finality
// essence), :2162-2173 (finality cubes).
export function plagueToFinality(game: GameState, ctx: TickCtx): void {
  void ctx

  // script.js:1985-1998 — cosmicPlaguePerSecond (achievement multipliers at
  // :1988-1989; the rest of the unlocks>=31 block, :1999-2009, is DOM-only).
  if (game.unlocks >= 31) {
    game.cosmicPlaguePerSecond = game.spores.pow(2)
    game.cosmicPlaguePerSecond = game.cosmicPlaguePerSecond.mul(game.plagueUpgradesBought[0].pow(1.3).add(1))
    if (game.unlockedAchievements[24] > 0) game.cosmicPlaguePerSecond = game.cosmicPlaguePerSecond.mul(5)
    if (game.unlockedAchievements[24] > 1) game.cosmicPlaguePerSecond = game.cosmicPlaguePerSecond.mul(5)
    if (game.dragonStage >= 9) game.cosmicPlaguePerSecond = game.cosmicPlaguePerSecond.mul(100)
    if (game.unlocks >= 32) {
      game.cosmicPlaguePerSecond = game.cosmicPlaguePerSecond.mul(1.5 ** game.oganessonUpgradesBought[1])
      game.cosmicPlaguePerSecond = game.cosmicPlaguePerSecond.mul(1.5 ** game.oganessonUpgradesBought[5])
    }
    if (game.unlocks >= 33) game.cosmicPlaguePerSecond = game.cosmicPlaguePerSecond.mul(new Dec(2).pow(game.lightEssenceUpgradesBought[1].pow(0.6)))
    if (game.unlocks >= 34) game.cosmicPlaguePerSecond = game.cosmicPlaguePerSecond.mul(new Dec(10).pow(game.deathEssenceUpgradesBought[3].pow(0.7)))
    if (game.unlocks >= 35 && (game.nuclearPastaState == 3 || game.nuclearPastaUpgradesBought[2])) game.cosmicPlaguePerSecond = game.cosmicPlaguePerSecond.mul(Dec.min(new Dec(10).pow(game.nuclearPasta.pow(0.7)), '1e1500'))
    else if (game.unlocks >= 35 && game.nuclearPastaState == 4) game.cosmicPlaguePerSecond = new Dec(0)
  }

  // script.js:2011-2016 — oganessonPerSecond (:2017-2025 DOM-only).
  if (game.unlocks >= 32) {
    game.oganessonPerSecond = Dec.max(game.plutonium.log10().div(1e9).floor(), 0)
    if (game.plagueUpgradesBought[4].gt(0)) game.oganessonPerSecond = game.oganessonPerSecond.mul(new Dec(2).pow(game.plagueUpgradesBought[4].pow(0.5)))
    if (game.unlockedAchievements[24] > 2) game.oganessonPerSecond = game.oganessonPerSecond.mul(10)
    if (game.unlocks >= 33) game.oganessonPerSecond = game.oganessonPerSecond.mul(new Dec(1.5).pow(game.darkEssenceUpgradesBought[1].pow(0.6)))
    if (game.unlocks >= 35 && game.nuclearPastaState == 5) game.oganessonPerSecond = game.oganessonPerSecond.mul(Dec.min(new Dec(1.5).pow(game.nuclearPasta.pow(0.6)), 1e30))
  }

  // script.js:2027-2053 — light/dark essence per second (:2054-2070 DOM-only).
  // Note :2040/:2053 read game.finalityCubeEffect, the value written at the
  // END of the previous tick (:2168-2171) — a one-tick lag that settles in
  // steady state.
  if (game.unlocks >= 33) {
    // script.js:2028-2040 — lightEssencePerSecond
    game.lightEssencePerSecond = game.darkEssence.add(1).log10().add(1)
    game.lightEssencePerSecond = game.lightEssencePerSecond.mul(new Dec(2).pow(game.lightEssenceUpgradesBought[0].pow(0.5)))
    game.lightEssencePerSecond = game.lightEssencePerSecond.mul(game.oganessonUpgradesBought[6] ** 1.2 * 2 + 1)
    if (game.unlockedAchievements[24] > 3) game.lightEssencePerSecond = game.lightEssencePerSecond.mul(5)
    if (game.unlocks >= 34) {
      game.lightEssencePerSecond = game.lightEssencePerSecond.mul(game.deathEssence.add(1).log10().mul(2).add(1))
      game.lightEssencePerSecond = game.lightEssencePerSecond.mul(new Dec(10000).pow(game.deathEssenceUpgradesBought[4].pow(0.5)))
    }
    if (game.dragonStage >= 10) game.lightEssencePerSecond = game.lightEssencePerSecond.mul(1000)
    if (game.unlocks >= 35 && (game.nuclearPastaState == 1 || game.nuclearPastaUpgradesBought[0])) game.lightEssencePerSecond = game.lightEssencePerSecond.mul(Dec.min(new Dec(3).pow(game.nuclearPasta.pow(0.7)), 1e75))
    else if (game.unlocks >= 35 && game.nuclearPastaState == 3) game.lightEssencePerSecond = new Dec(0)
    if (game.unlocks >= 36) game.lightEssencePerSecond = game.lightEssencePerSecond.mul(new Dec(10000).pow(game.finalityEssenceUpgradesBought[3].pow(0.5)))
    if (game.unlocks >= 37) game.lightEssencePerSecond = game.lightEssencePerSecond.mul(game.finalityCubeEffect)
    // script.js:2041-2053 — darkEssencePerSecond (mirror of the light block)
    game.darkEssencePerSecond = game.lightEssence.add(1).log10().add(1)
    game.darkEssencePerSecond = game.darkEssencePerSecond.mul(new Dec(2).pow(game.darkEssenceUpgradesBought[0].pow(0.5)))
    game.darkEssencePerSecond = game.darkEssencePerSecond.mul(game.oganessonUpgradesBought[6] ** 1.2 * 2 + 1)
    if (game.unlockedAchievements[24] > 3) game.darkEssencePerSecond = game.darkEssencePerSecond.mul(5)
    if (game.unlocks >= 34) {
      game.darkEssencePerSecond = game.darkEssencePerSecond.mul(game.deathEssence.add(1).log10().mul(2).add(1))
      game.darkEssencePerSecond = game.darkEssencePerSecond.mul(new Dec(10000).pow(game.deathEssenceUpgradesBought[4].pow(0.5)))
    }
    if (game.dragonStage >= 10) game.darkEssencePerSecond = game.darkEssencePerSecond.mul(1000)
    if (game.unlocks >= 35 && (game.nuclearPastaState == 1 || game.nuclearPastaUpgradesBought[0])) game.darkEssencePerSecond = game.darkEssencePerSecond.mul(Dec.min(new Dec(3).pow(game.nuclearPasta.pow(0.7)), 1e75))
    else if (game.unlocks >= 35 && game.nuclearPastaState == 3) game.darkEssencePerSecond = new Dec(0)
    if (game.unlocks >= 36) game.darkEssencePerSecond = game.darkEssencePerSecond.mul(new Dec(10000).pow(game.finalityEssenceUpgradesBought[3].pow(0.5)))
    if (game.unlocks >= 37) game.darkEssencePerSecond = game.darkEssencePerSecond.mul(game.finalityCubeEffect)
  }

  // script.js:2072-2081 — deathEssencePerSecond. The rest of the unlocks>=34
  // block (:2082-2113) is DOM-only, including the verbose local
  // deathEssenceUpgrade3Effect display computation at :2091-2105. QUIRK: that
  // display block guards on deathEssenceUpgradesBought[2].gt(0) alone, while
  // the terse engine copy of the same formula (script.js:1592, sigil section)
  // guards on unlocks >= 34 as well — display and engine can disagree when
  // the upgrade count survives a reset that drops unlocks below 34. DOM-only,
  // so nothing of it is transcribed here.
  if (game.unlocks >= 34) {
    game.deathEssencePerSecond = game.darkEssence.add(game.lightEssence).pow(0.4).div(100)
    game.deathEssencePerSecond = game.deathEssencePerSecond.mul(new Dec(2).pow(game.deathEssenceUpgradesBought[0].pow(0.5)))
    if (game.unlocks >= 35 && (game.nuclearPastaState == 2 || game.nuclearPastaUpgradesBought[1])) game.deathEssencePerSecond = game.deathEssencePerSecond.mul(Dec.min(new Dec(2.5).pow(game.nuclearPasta.pow(0.6)), 1e7))
    else if (game.unlocks >= 35 && game.nuclearPastaState == 1) game.deathEssencePerSecond = new Dec(0)
    if (game.unlockedAchievements[24] > 4) game.deathEssencePerSecond = game.deathEssencePerSecond.mul(10)
    if (game.unlocks >= 36) game.deathEssencePerSecond = game.deathEssencePerSecond.mul(game.finalityEssence.add(1).log10().mul(2).add(1))
    if (game.deathEssencePerSecond.gt(1e20)) game.deathEssencePerSecond = game.deathEssencePerSecond.mul(1e20).pow(0.5)
    if (game.dragonStage >= 11) game.deathEssencePerSecond = game.deathEssencePerSecond.mul(100)
    if (game.unlocks >= 37) game.deathEssencePerSecond = game.deathEssencePerSecond.mul(game.finalityCubeEffect)
  }

  // script.js:2115-2142 — nuclear pasta block: ENTIRELY DOM-only (resource
  // text, state names, and a switch that only writes #nuclearPastaEffect).
  // No game.* writes; deliberately skipped.

  // script.js:2143-2151 — finalityEssencePerSecond (:2152-2160 DOM-only).
  if (game.unlocks >= 36) {
    game.finalityEssencePerSecond = game.deathEssence.add(game.lightEssence).pow(0.3).div(1e16)
    game.finalityEssencePerSecond = game.finalityEssencePerSecond.mul(new Dec(2).pow(game.finalityEssenceUpgradesBought[0].pow(0.5)))
    if (game.dragonStage >= 11) game.finalityEssencePerSecond = game.finalityEssencePerSecond.mul(100)
    if (game.unlockedAchievements[24] > 8) game.finalityEssencePerSecond = game.finalityEssencePerSecond.mul(100)
    if (game.unlocks >= 37) game.finalityEssencePerSecond = game.finalityEssencePerSecond.mul(game.finalityCubeEffect)
    if (game.unlockedAchievements[24] > 9) game.finalityEssencePerSecond = game.finalityEssencePerSecond.mul(100)
    if (game.unlockedAchievements[24] > 10) game.finalityEssencePerSecond = game.finalityEssencePerSecond.mul(1e7)
    if (game.unlockedAchievements[24] > 11) game.finalityEssencePerSecond = game.finalityEssencePerSecond.mul(1e28)
  }

  // script.js:2162-2173 — finalityCubeEffect at :2168-2171 (surrounding
  // lines are DOM-only cost/resource text).
  if (game.unlocks >= 37) {
    game.finalityCubeEffect = new Dec(100).pow(game.finalityCubes.pow(0.5))
    game.finalityCubeEffect = game.finalityCubeEffect.mul(new Dec(50000).pow(game.finalityBoosts.pow(0.8)))
    game.finalityCubeEffect = game.finalityCubeEffect.mul(new Dec(100000).pow(game.finalityBoostBoosts.pow(0.7)))
    if (game.finalityCubeEffect.gt(1e100)) game.finalityCubeEffect = new Dec(1e100)
  }
}
